import CalendarIcon from "@mui/icons-material/CalendarToday";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Delete from "@mui/icons-material/Delete";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ListIcon from "@mui/icons-material/FormatListBulleted";
import {
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  type SvgIconProps,
} from "@mui/material";
import {
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridActionsCellItem,
  GridRenderCellParams,
  GridRowParams,
} from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { CellText } from "components/Omnisearch/helpComponents/CellText";
import {
  OmnisearchCommandButtonsContext,
  OmnisearchDataDisplay,
} from "components/OmnisearchDataDisplay";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { TagsDisplay } from "components/TagsDisplay";
import { getFullName } from "encamp-shared/src/utils/name";
import {
  EventMutationScope,
  Frequency,
  TaskStatus,
  TasksQuery,
  VirtualEventStatus,
} from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { DateTime } from "luxon";
import { OmnisearchProvider as LegacyOmnisearchProvider } from "providers/OmnisearchContext";
import { OmnisearchProvider } from "providers/OmnisearchProvider";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { prettyPrintDateMed } from "util/dates";

import { EditTaskDialog } from "./EditTaskDialog";
import useDeleteTaskMutation from "./hooks/useDeleteTask";
import { ListModeButton, type ListMode } from "./ListModeButton";
import RecurrenceScopeRadioDialog from "./RecurrenceScopeRadioDialog";
import { TASK_LIST_REFETCH_QUERIES, TASK_SERIES_QUERY, TASKS_QUERY } from "./schema";
import { getSeriesGridSx, SERIES_TOGGLE_WIDTH } from "./seriesGroupStyles";
import { getOverdueTooltip, taskStatusToChipSx } from "./statusStyles";
import { TasksCalendar } from "./TasksCalendar";
import { TaskSeriesDetailPanel } from "./TaskSeriesDetailPanel";
import { buildBeforeAfterSearchString } from "./util.buildSearchString";
import { taskStatusToLabel } from "./util.labels";

export type Row = TasksQuery["virtualEvents"]["items"][0];

type DisplayMode = "list" | "calendar";

type SeriesToggleIconProps = { className?: string; fontSize?: SvgIconProps["fontSize"] };

/** Same chevron as the left nav: right when collapsed, rotated down when expanded. */
function SeriesExpandIcon({ className, fontSize }: SeriesToggleIconProps) {
  return <ChevronRight className={className} fontSize={fontSize} />;
}

function SeriesCollapseIcon({ className, fontSize }: SeriesToggleIconProps) {
  return (
    <ChevronRight className={className} fontSize={fontSize} sx={{ transform: "rotate(90deg)" }} />
  );
}

function sortTagsAlphabetically(tags: Array<{ id: string; name: string }>) {
  return [...tags].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
}

const defaultDeleteTaskState = {
  open: false,
  recurring: false,
  virtualEvent: null,
};

export type TasksDisplayProps = {
  additionalFilterColumns: FilterKey[];
  commandButtons: (context: OmnisearchCommandButtonsContext) => React.ReactNode[];
  defaultSearch: string;
  /** If not provided, the default search will be used for the calendar view. */
  defaultCalendarSearch?: string;
  persistenceKey?: string;
  /** Tenant whose tasks are shown; used by the Series view to load occurrences. */
  tenantId?: string;
  /** Optional content rendered above the grid, inside the OmnisearchProvider so
   *  it can use `useOmnisearch()` to read/edit the omnisearch query. */
  topContent?: ReactNode;
};

export const TasksDisplay = ({
  additionalFilterColumns,
  commandButtons,
  defaultSearch,
  defaultCalendarSearch,
  persistenceKey,
  tenantId,
  topContent,
}: TasksDisplayProps) => {
  const alerts = useAlerts();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { hasTaskWritePermissionForFacilities } = useAuthorization();

  const [displayMode, setDisplayMode] = useState<DisplayMode>("list");
  // Remembered per grid, like its column settings, so it survives a refresh or navigating away.
  const [storedListMode, setListMode] = useLocalStorageState<ListMode>(
    `${persistenceKey ?? "tasks"}-list-mode`,
    { defaultValue: "series" }
  );
  const listMode: ListMode = storedListMode === "list" ? "list" : "series";
  const viewMode = displayMode === "calendar" ? "calendar" : listMode;

  const [calendarRange, setCalendarRange] = useState<string>(() =>
    buildBeforeAfterSearchString(DateTime.local())
  );

  useEffect(() => {
    setCalendarRange(buildBeforeAfterSearchString(DateTime.local()));
  }, []);

  const onChangeDisplayMode = useCallback(
    (event: React.MouseEvent<HTMLElement>, newDisplayMode: DisplayMode | null) => {
      // Ignore deselecting the active button; a view must always be selected.
      if (newDisplayMode !== null) setDisplayMode(newDisplayMode);
    },
    []
  );

  const [deleteTaskState, setDeleteTaskState] = useState<{
    open: boolean;
    recurring: boolean;
    virtualEvent: Pick<Row, "taskId" | "deadline" | "title"> | null;
  }>(defaultDeleteTaskState);
  const resetDeleteTaskState = () => setDeleteTaskState(defaultDeleteTaskState);

  const [deleteTask, { loading: deleteTaskLoading }] = useDeleteTaskMutation();

  const {
    selectedId: virtualEventId,
    pushIdToUrl: pushVirtualEventIdToUrl,
    popIdFromUrl: removeVirtualEventIdFromUrl,
  } = useSelectedIdInUrl("virtualEventId");

  const onRowEdit = useCallback(
    (row: Row) => {
      if (!hasTaskWritePermissionForFacilities(row.facilities?.map((f) => f.id) ?? [])) {
        return; // no op because the user doesn't have permissions
      }

      pushVirtualEventIdToUrl(row.id);
    },
    [hasTaskWritePermissionForFacilities, pushVirtualEventIdToUrl]
  );

  const onDeleteTask = useCallback(
    (scope: EventMutationScope) => {
      if (deleteTaskState.virtualEvent) {
        deleteTask({
          variables: {
            input: {
              taskId: deleteTaskState.virtualEvent.taskId,
              date: deleteTaskState.virtualEvent.deadline,
              eventMutationScope: scope,
            },
          },
          onCompleted: () => {
            resetDeleteTaskState();
            alerts.success(`Event deleted`);
          },
          onError: (error) => {
            alerts.error(`Error deleting event: ${error.message}`);
          },
          refetchQueries: TASK_LIST_REFETCH_QUERIES,
        });
      }
    },
    [deleteTask, deleteTaskState.virtualEvent, alerts]
  );

  // Optional subtitle sits beneath the date in smaller, secondary text.
  const renderDeadlineCell = (value: Row["deadline"], row: Row, subtitle?: string) => {
    const overdue = row.status === VirtualEventStatus.Overdue;
    return (
      <Stack
        sx={{
          paddingTop: theme.spacing(1),
          paddingBottom: theme.spacing(1),
          height: "100%",
          width: "100%",
          alignItems: "start",
        }}
      >
        <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
          {overdue && (
            <Tooltip title={getOverdueTooltip(row.deadline)} arrow>
              <ErrorOutlineIcon sx={{ fontSize: 16, flexShrink: 0 }} color="error" />
            </Tooltip>
          )}
          <Typography
            variant="body2"
            color={overdue ? "error" : undefined}
            fontStyle={overdue ? "italic" : undefined}
          >
            {prettyPrintDateMed(value)}
          </Typography>
        </Stack>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Stack>
    );
  };

  const columns: OmnisearchGridColDef<Row>[] = [
    {
      field: "deadline",
      headerName: "Deadline",
      sortable: true,
      flex: 0.7,
      renderCell: ({ value, row }) => renderDeadlineCell(value, row),
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1.5,
      sortable: true,
      renderCell: ({ row: { title } }) => {
        return <CellText variant="body2">{title}</CellText>;
      },
    },
    {
      field: "facilities",
      headerName: "Facilities",
      flex: 1.5,
      sortable: true,
      renderCell({ row }) {
        return (
          <Stack
            spacing={0}
            style={{
              paddingTop: theme.spacing(1),
              paddingBottom: theme.spacing(1),
              height: "100%",
              width: "100%",
              alignItems: "start",
              overflow: "hidden",
            }}
          >
            {(row.facilities ?? []).map((facility) => (
              <Typography
                key={facility.id}
                variant="body2"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: "inherit",
                  whiteSpace: "nowrap",
                }}
              >
                <Tooltip title={facility.name}>
                  <span>{facility.name}</span>
                </Tooltip>
              </Typography>
            ))}
          </Stack>
        );
      },
    },
    {
      field: "taskStatus",
      headerName: "Status",
      flex: 0.7,
      sortable: true,
      renderCell({ row: { taskStatus } }) {
        const stylingBlock = taskStatusToChipSx(taskStatus, theme);
        return <Chip label={taskStatusToLabel(taskStatus)} sx={stylingBlock} size="small" />;
      },
      filterKeyType: "enumAutocomplete",
      enumValues: Object.values(TaskStatus),
      enumPresentationFunction: (enumValue) => taskStatusToLabel(enumValue),
    },

    {
      field: "assignee",
      headerName: "Assignee",
      sortable: true,
      flex: 1,
      valueGetter(_, row) {
        return getFullName(row.assignee?.person) || row.assignee?.email;
      },
      renderCell: ({ value }) => {
        return <CellText variant="body2">{value}</CellText>;
      },
    },
    {
      field: "tags",
      headerName: "Tags",
      flex: 0.7,
      filterKeyType: "taskTags",
      renderCell: ({ row }) => (
        <TagsDisplay
          tags={sortTagsAlphabetically(
            row.tags?.map((tag) => ({ id: tag.id, name: tag.name })) ?? []
          )}
        />
      ),
    },
    {
      field: "actions",
      type: "actions",
      sortable: false,
      align: "right",
      width: 30,
      getActions: ({ row }) => {
        const actions = [
          <Tooltip title="Delete Task" key="delete">
            <GridActionsCellItem
              onClick={() =>
                setDeleteTaskState({
                  open: true,
                  recurring: row.frequency !== Frequency.Once,
                  virtualEvent: row,
                })
              }
              label="Delete Task"
              icon={<Delete />}
            />
          </Tooltip>,
        ];

        return hasTaskWritePermissionForFacilities(row.facilities?.map((f) => f.id) ?? [])
          ? actions
          : [];
      },
    },
  ];

  // Expand/collapse toggle, with a tooltip, replacing MUI's default toggle column.
  // Width is pinned so the detail panel's leading spacer can match it exactly.
  const detailPanelToggleColumn: OmnisearchGridColDef<Row> = {
    ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    width: SERIES_TOGGLE_WIDTH,
    minWidth: SERIES_TOGGLE_WIDTH,
    renderCell: (params) =>
      params.row.isRecurring ? (
        <Tooltip title="Show upcoming occurrences">
          <span>{GRID_DETAIL_PANEL_TOGGLE_COL_DEF.renderCell?.(params)}</span>
        </Tooltip>
      ) : null,
  };

  // In the Series view each row is a series' next instance; the deadline column
  // reflects that, and the rest of the operations stay identical to the list.
  const seriesColumns: OmnisearchGridColDef<Row>[] = [
    detailPanelToggleColumn,
    ...columns.map((column) =>
      column.field === "deadline"
        ? {
            ...column,
            headerName: "Next deadline",
            renderCell: ({ value, row }: GridRenderCellParams<Row>) =>
              renderDeadlineCell(value, row, row.isRecurring ? "Recurring" : undefined),
          }
        : column
    ),
  ];

  // The detail panel renders occurrence rows aligned to the parent grid: it reads
  // the parent's live visible columns (widths, order, manage-columns visibility)
  // and reuses these defs only for how each cell renders. One-off tasks have no
  // other occurrences, so they get no panel (or toggle).
  const getSeriesDetailPanelContent = ({ row }: GridRowParams<Row>) =>
    row.isRecurring && (
      <TaskSeriesDetailPanel
        seriesId={row.seriesId}
        tenantId={tenantId}
        afterDeadline={row.deadline}
        columns={columns}
        onRowClick={onRowEdit}
      />
    );

  const viewModeToggle = [
    <ToggleButtonGroup
      key="view-mode-toggle-button-group"
      value={displayMode}
      exclusive
      color="primary"
      size="large"
      onChange={onChangeDisplayMode}
    >
      <Tooltip title="List">
        <ToggleButton value="list">
          <ListIcon />
        </ToggleButton>
      </Tooltip>
      <Tooltip title="Calendar">
        <ToggleButton value="calendar">
          <CalendarIcon />
        </ToggleButton>
      </Tooltip>
    </ToggleButtonGroup>,
  ];

  const commandButtonsWithListMode = useCallback(
    (context: OmnisearchCommandButtonsContext) => [
      <ListModeButton key="list-mode" value={listMode} onChange={setListMode} />,
      ...commandButtons(context),
    ],
    [commandButtons, listMode, setListMode]
  );

  const isSeriesView = viewMode === "series";
  const seriesGridSx = useMemo(() => getSeriesGridSx(theme), [theme]);
  const gridPersistenceKey = persistenceKey ? `${persistenceKey}-table` : "tasks-table";
  const activePersistenceKey = isSeriesView ? `${gridPersistenceKey}-series` : gridPersistenceKey;
  const activeColumns = isSeriesView ? seriesColumns : columns;

  return (
    <OmnisearchProvider
      persistenceKey={activePersistenceKey}
      columns={activeColumns}
      isURLDriven
      initialSortModel={[{ field: "deadline", sort: "asc" }]}
      initialPageSize={10}
      additionalFilters={additionalFilterColumns}
      excludeFilters={["deadline", "facilities"]}
      isMobile={isMobile}
      initialColumnVisibilityModel={{
        ...(isMobile && {
          facilities: false,
          tags: false,
        }),
      }}
    >
      {topContent}
      {viewMode === "series" ? (
        <OmnisearchDataGrid
          key="tasks-series-grid"
          persistenceKey={activePersistenceKey}
          noDataMessage="No recurring task series match your filters."
          isURLDriven
          columns={seriesColumns}
          dataQuery={TASK_SERIES_QUERY}
          defaultSearch={defaultSearch}
          getItems={(data) => data.taskSeries.items}
          getCount={(data) => data.taskSeries.count ?? 0}
          additionalFilterColumns={additionalFilterColumns}
          isRowSelectable={() => false}
          onRowClick={({ row }) => onRowEdit(row)}
          getDetailPanelContent={getSeriesDetailPanelContent}
          getDetailPanelHeight={() => "auto"}
          gridSx={seriesGridSx}
          slots={{
            detailPanelExpandIcon: SeriesExpandIcon,
            detailPanelCollapseIcon: SeriesCollapseIcon,
          }}
          iconToolbarButtons
          startCommandButtons={viewModeToggle}
          commandButtons={commandButtonsWithListMode}
          rowHeight={"auto"}
        />
      ) : viewMode === "list" ? (
        <OmnisearchDataGrid
          key="tasks-list-grid"
          persistenceKey={activePersistenceKey}
          noDataMessage="No tasks have been added to this organization yet."
          isURLDriven
          columns={columns}
          dataQuery={TASKS_QUERY}
          defaultSearch={defaultSearch}
          getItems={(data) => data.virtualEvents.items}
          getCount={(data) => data.virtualEvents.count ?? 0}
          additionalFilterColumns={additionalFilterColumns}
          isRowSelectable={() => false}
          onRowClick={({ row }) => onRowEdit(row)}
          iconToolbarButtons
          startCommandButtons={viewModeToggle}
          commandButtons={commandButtonsWithListMode}
          rowHeight={"auto"}
        />
      ) : (
        <LegacyOmnisearchProvider>
          <OmnisearchDataDisplay
            persistenceKey={persistenceKey ? `${persistenceKey}-calendar` : "tasks-calendar"}
            isURLDriven
            columns={columns}
            dataQuery={TASKS_QUERY}
            dataQueryVariables={{ pageSize: undefined, page: undefined }}
            defaultSearch={`${defaultCalendarSearch ?? defaultSearch} ${calendarRange}`}
            getItems={(data) => data.virtualEvents.items}
            getCount={(data) => data.virtualEvents.count ?? 0}
            excludeFilterColumns={["deadline", "facilities"]}
            additionalFilterColumns={additionalFilterColumns}
            iconToolbarButtons
            startCommandButtons={viewModeToggle}
            commandButtons={commandButtons}
            dataDisplay={({ items, loading }) => (
              <TasksCalendar
                loading={loading}
                tasks={items}
                onTaskClick={onRowEdit}
                onRangeChange={setCalendarRange}
              />
            )}
          />
        </LegacyOmnisearchProvider>
      )}

      {virtualEventId && (
        <EditTaskDialog virtualEventId={virtualEventId} onClose={removeVirtualEventIdFromUrl} />
      )}
      {deleteTaskState.recurring && (
        <RecurrenceScopeRadioDialog
          action="Delete"
          open={deleteTaskState.open}
          onClose={resetDeleteTaskState}
          onSubmit={onDeleteTask}
          loading={deleteTaskLoading}
        />
      )}
      {deleteTaskState.open && !deleteTaskState.recurring && (
        <ConfirmDialog
          open={deleteTaskState.open}
          onClose={resetDeleteTaskState}
          onConfirm={() => onDeleteTask(EventMutationScope.This)}
          header="Are you sure you want to delete this task?"
          resources={[
            {
              resourceType: "Task",
              resourceName: deleteTaskState.virtualEvent?.title,
            },
          ]}
          loading={deleteTaskLoading}
        />
      )}
    </OmnisearchProvider>
  );
};
